/**
 * Copyright ©2023 Drivepoint
 */

import {FirebaseApp, initializeApp} from "firebase/app";
import {Auth, browserLocalPersistence, browserPopupRedirectResolver, confirmPasswordReset,
  fetchSignInMethodsForEmail, getAuth, GoogleAuthProvider, initializeAuth, isSignInWithEmailLink,
  linkWithCredential, linkWithPopup, linkWithRedirect, OAuthCredential, OAuthProvider, setPersistence,
  signInWithCustomToken, signInWithEmailAndPassword, signInWithEmailLink, signInWithPopup, signOut, unlink,
  Unsubscribe, User, UserCredential, verifyPasswordResetCode} from "firebase/auth";
import * as firebaseui from "firebaseui";
import {ObjectUtilities, Utilities} from "@bainbridge-growth/node-common";
import {Config} from "@bainbridge-growth/node-frontend";
import {Maybe} from "@dp-types/Maybe";
import WebAppServerClient from "@services/clients/WebAppServerClient";
import {getFirebaseSignInFlow} from "@utilities/firebaseSignInFlow";
import {EventBus} from "../eventbus/EventBus";
import ServiceRegistry from "../ServiceRegistry";
import State from "../state/State";
import DrivepointUser from "../user/DrivepointUser";
import FirebaseEvent from "./FirebaseEvent";

export enum FirebaseAuthenticationState {
  INITIALIZING = "initializing",
  AUTHENTICATED = "authenticated",
  UNAUTHENTICATED = "unauthenticated"
}

export default class Firebase {

  private static MAX_LOAD_USER_RETRIES = 5;

  private static _app: FirebaseApp;
  private static _auth: Auth;
  private static _ui?: firebaseui.auth.AuthUI;
  private static _auth_unsubscribe: Unsubscribe;
  private static _user: User | null;
  private static _token?: string;
  private static _role?: string;
  private static _claims?: any;
  private static _signInProvider?: string | null;
  private static _autoRefreshInterval?: any;
  private static _autoSetUpUser: boolean = true;

  static async init(): Promise<void> {
    State.set("authentication", FirebaseAuthenticationState.INITIALIZING);
    State.register("excel_user", Firebase.onExcelUserChanged);
    const options = {...Config.get("firebase")};
    // Same-origin authDomain is required for signInWithRedirect on Chrome 115+. A sibling
    // custom domain (auth-prod.drivepoint.io vs app.drivepoint.io) is still cross-origin,
    // so Chrome bounce-tracking / storage partitioning drops the redirect result and
    // FirebaseUI retries Google — a redirect loop. Only the redirect flow (iOS + embedded
    // WebViews) hits that loop; the popup flow (desktop + Android Chrome) is unaffected and
    // works against the configured sibling authDomain, which is already a registered Google
    // OAuth redirect URI. Same-origin requires each deployed host's /__/auth/handler to be
    // registered with Google, so we only opt into it when the redirect flow will be used.
    // Localdev always uses the page host because Vite proxies /__/auth/* to
    // <projectId>.firebaseapp.com; deployed envs serve /__/auth/* via Firebase Hosting (nginx
    // proxies that path for the legacy Docker image).
    if (typeof window !== "undefined") {
      const usesRedirectFlow = getFirebaseSignInFlow() === "redirect";
      if (Config.get("environment") === "localdev" || usesRedirectFlow) {
        options.authDomain = window.location.host;
      }
    }
    Firebase._app = initializeApp(options, `${options.projectId}.${Config.get("environment")}`);
    try {
      Firebase._auth = initializeAuth(Firebase._app, {
        persistence: browserLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver
      });
    } catch (error: any) {
      logger.debug("initializeAuth failed, falling back to getAuth", error?.message);
      Firebase._auth = getAuth(Firebase._app);
      try {
        await setPersistence(Firebase._auth, browserLocalPersistence);
      } catch (persistenceError: any) {
        logger.debug("setPersistence(browserLocalPersistence) failed", persistenceError?.message);
      }
    }
    Firebase._auth_unsubscribe = Firebase._auth.onAuthStateChanged(Firebase.onAuthStateChanged);
    Firebase.autoRefreshToken();
  }

  static async stop(): Promise<void> {
    Firebase.reset();
    Firebase._auth_unsubscribe();
    clearInterval(Firebase._autoRefreshInterval);
  }

  static async onAuthStateChanged(user: User | null): Promise<void> {
    Firebase._user = user;
    if (user) {
      await Firebase.setUpUser(user);
    } else {
      Firebase.reset();
    }
  }

  static onExcelUserChanged(event: any): void {
    let user = State.get("user") as DrivepointUser;
    if (!user?.excelUser) { return; }
    user = ObjectUtilities.clone(user); // clone to avoid changing the value in State
    user.excelUser = event.value;
    State.set("user", user);
  }

  static autoRefreshToken(): void {
    Firebase._autoRefreshInterval = setInterval(async () => {
      try {
        if (Firebase._user) {
          const result = await Firebase._user.getIdTokenResult();
          const now = Date.now() / 1000;
          const exp = parseFloat(result.claims.exp || `${now}`);
          const ttl = Math.floor(exp - now);
          if (ttl < Config.get("token.refresh_seconds", 600)) {
            logger.debug("refreshing auth token");
            Firebase.token = await Firebase._user.getIdToken(true);
          }
        }
      } catch (error: any) {
        logger.error(error.message);
      }
    }, Config.get("token.check_seconds", 60000));
  }

  static async setUpUser(user: User) {
    if (!Firebase.autoSetUpUser) { return; }
    ServiceRegistry.serverSentEventService.disconnect();
    Firebase.token = await user?.getIdToken();
    const result = await user?.getIdTokenResult();
    Firebase._signInProvider = result.signInProvider;
    Firebase._claims = result?.claims;
    Firebase._role = result?.claims?.role as string;
    await this.loadBainbridgeUser(user);
  }

  static async loadBainbridgeUser(user: User): Promise<void> {
    let retries = 0;
    while (true) {
      if (!Firebase.autoSetUpUser) { return; }
      try {
        const drivepointUser = new DrivepointUser(user);
        await drivepointUser.load();
        drivepointUser.excelUser._groups = drivepointUser.excelUser.groups; // cache the base groups
        State.set("user", drivepointUser);
        ServiceRegistry.serverSentEventService.connect();
        State.set("authentication", FirebaseAuthenticationState.AUTHENTICATED);
        return;
      } catch (error: any) {
        logger.error(error.message);
        if (retries >= Firebase.MAX_LOAD_USER_RETRIES) {
          EventBus.dispatch(new FirebaseEvent("load_bainbridge_user:error", {user: user}));
          throw new Error("Could not connect");
        }
        EventBus.dispatch(new FirebaseEvent("load_bainbridge_user:retry", {user: user, retries: retries}));
        await Utilities.sleep(5000);
        retries++;
      }
    }
  }

  static reset(): void {
    State.set("authentication", FirebaseAuthenticationState.UNAUTHENTICATED);
    State.set("user", undefined);
    ServiceRegistry.serverSentEventService.disconnect();
    Firebase.token = undefined;
    Firebase._claims = undefined;
    Firebase._role = undefined;
    Firebase._signInProvider = undefined;
  }

  static get auth(): Auth {
    return Firebase._auth;
  }

  static get user(): User | null {
    return Firebase._user;
  }

  static get ui(): firebaseui.auth.AuthUI {
    if (!Firebase._ui) {
      Firebase._ui = new firebaseui.auth.AuthUI(Firebase.auth);
    }
    return Firebase._ui;
  }

  static get token(): string | undefined {
    return Firebase._token;
  }

  static set token(value: string | undefined) {
    Firebase._token = value;
    State.set("firebase_token", Firebase._token);
  }

  static get role(): string | undefined {
    return Firebase._role;
  }

  static get claims(): any {
    return Firebase._claims;
  }

  static get signInProvider(): Maybe<string | null> {
    return Firebase._signInProvider;
  }

  static get autoSetUpUser(): boolean {
    return Firebase._autoSetUpUser;
  }

  static set autoSetUpUser(value: boolean) {
    Firebase._autoSetUpUser = value;
  }

  static async signIn(email: string, password: string): Promise<UserCredential | undefined> {
    if (Firebase._auth) {
      return signInWithEmailAndPassword(Firebase._auth, email, password);
    }
  }

  static async signInWithCustomToken(token: string): Promise<UserCredential | undefined> {
    if (Firebase._auth) {
      return signInWithCustomToken(Firebase._auth, token);
    }
  }

  static async signOut(): Promise<void> {
    if (Firebase._auth) {
      return signOut(Firebase._auth);
    }
  }

  static async reload(): Promise<void> {
    if (Firebase.user) {
      return Firebase.setUpUser(Firebase.user);
    }
  }

  static isSignInWithEmailLink(link: string): boolean {
    return Firebase._auth ? isSignInWithEmailLink(Firebase._auth, link) : false;
  }

  static async signInWithEmailLink(email: string): Promise<UserCredential> {
    return signInWithEmailLink(Firebase._auth, email);
  }

  static async verifyPasswordResetCode(code: string): Promise<string> {
    return verifyPasswordResetCode(Firebase._auth, code);
  }

  static confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    return confirmPasswordReset(Firebase._auth, oobCode, newPassword);
  }

  static async emailExistAlready(email: string) {
    return (await fetchSignInMethodsForEmail(Firebase._auth, email)).length > 0;
  }

  static async linkMicrosoftAccount(): Promise<void> {
    await Firebase.auth.currentUser!.reload();
    const provider = this._getMicrosoftProvider();
    try {
      await linkWithPopup(Firebase.auth.currentUser!, provider);
      await Firebase.clearUserCacheAndReload({type: "link", providerId: "microsoft.com"});
    } catch (error: any) {
      await this._handleLinkingError(error, provider, "microsoft.com");
    }
  }

  static async linkGoogleAccount(): Promise<void> {
    await Firebase.auth.currentUser!.reload();
    const provider = this._getGoogleProvider();
    try {
      await linkWithPopup(Firebase.auth.currentUser!, provider);
      await Firebase.clearUserCacheAndReload({type: "link", providerId: "google.com"});
    } catch (error: any) {
      await this._handleLinkingError(error, provider, "google.com");
    }
  }

  private static async _handleLinkingError(error: any, provider: OAuthProvider | GoogleAuthProvider, providerId: string): Promise<void> {
    logger.debug(error);
    if (error.code === "auth/popup-blocked") { return await linkWithRedirect(Firebase.auth.currentUser!, provider); }
    if (error.code === "auth/email-already-in-use") {
      const credential = OAuthProvider.credentialFromError(error);
      if (credential) { return await this._signInWithProvider(credential, provider, providerId); }
    }
    throw error;
  }

  private static async _signInWithProvider(pendingCredential: OAuthCredential, provider: OAuthProvider | GoogleAuthProvider, providerId: string): Promise<void> {
    let otherUser: UserCredential;
    try {
      otherUser = await signInWithPopup(Firebase.auth, provider);
      await linkWithCredential(otherUser.user, pendingCredential);
      await Firebase.clearUserCacheAndReload({type: "link", providerId});
    } catch (error: any) {
      logger.error(error);
      if (error.code === "auth/popup-blocked") { return await linkWithRedirect(Firebase.auth.currentUser!, provider); }
      throw error;
    }
  }

  private static _getGoogleProvider(): GoogleAuthProvider {
    return new GoogleAuthProvider();
  }

  private static _getMicrosoftProvider(): OAuthProvider {
    const provider = new OAuthProvider("microsoft.com");
    provider.addScope("email");
    provider.addScope("offline_access");
    provider.addScope("Files.ReadWrite.All");
    provider.addScope("User.Read");
    provider.setCustomParameters({prompt: "select_account"});
    return provider;
  }

  static async unlink(providerId: string): Promise<void> {
    if (!Firebase.auth.currentUser) { return; }
    await unlink(Firebase.auth.currentUser, providerId);
    await Firebase.clearUserCacheAndReload({type: "unlink", providerId});
  }

  static async clearUserCacheAndReload(reason?: any): Promise<void> {
    try {
      await WebAppServerClient.request(`/api/cache/${Firebase.auth.currentUser!.uid}`, "DELETE");
      if (reason?.type === "link" && reason?.providerId === "microsoft.com") {
        try { await WebAppServerClient.syncMicrosoftGrants(); } catch (error: any) { logger.debug(error); }
      }
      if (reason) { sessionStorage.setItem("reload-reason", JSON.stringify(reason)); }
      window.location.reload();
    } catch (error: any) {
      logger.error(error);
    }
  }

}
